import { ColumnDef } from "@tanstack/react-table";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import {
	ChevronDown,
	ChevronRight,
	Share2,
	Send,
	MoreHorizontal,
	EyeIcon,
	AlertCircle,
	ListChecks,
	PauseCircle,
	BellRing,
} from "lucide-react";
import { InvoiceType } from "../../../types/database-enums/invoices-enums";
import { IconButton, Tooltip } from "@mui/material";
import { getClientDisplayName } from "@/lib/utils/client-utils";
import { SharedMenuContent } from "./invoice-context-menu.component";
import {
	TablePaymentRow,
	isPaymentRow,
	BaseTableRowData,
	isInvoiceRow,
	TableInvoiceRow,
	InvoiceReminderActions,
	toReminderInvoiceRef,
} from "../types/invoice-table.types";
import { TooltipWrapper } from "../../../components/ui/tooltip-wrapper";
import { getReminderStageLabel } from "@/lib/utils/reminder-stage-label";
import { formatReminderDate } from "../mahnwesen/utils/reminder-placeholders";
import {
	PaymentJournalRowWithSubRows,
	InvoiceAllocationSubRow,
	isPaymentPrimaryRow,
	isInvoiceSubRow,
	PaymentJournalTableRowData,
} from "../types/payment-journal.types";
import {
	calculateVATBreakdown,
	calculateVATGrandTotals,
	VATCalculationMode,
} from "../utils/vat-calculations";
import {
	DropdownMenu,
	DropdownMenuTrigger,
	DropdownMenuContent,
} from "../../../components/ui/dropdown-menu";
import { ClientEntityType } from "@/lib/supabase/supabaseTypes";
import {
	MapPaymentTypeToString,
	PaymentType,
} from "@/dentlab/src/types/database-enums/payment-enums";
import { applyRounding } from "@/dentlab/src/lib/utils/calculate";
import { RoundToType } from "@/dentlab/src/store/Jobs/utils";
import { roundCurrency } from "../utils/payment-calculations";
import { cn } from "@/dentlab/src/lib/utils";

// Extend TanStack Table meta for filter configuration
declare module "@tanstack/react-table" {
	interface ColumnMeta<TData, TValue> {
		filterVariant?: "text" | "number" | "select" | "range" | "date";
		filterOptions?: { label: string; value: string }[];
	}
}

// Payment column definition for subrows
export interface PaymentColumnDef {
	id: string;
	header: string;
	width?: number;
	minWidth?: number;
	maxWidth?: number;
	render: (
		payment: TablePaymentRow,
		onActionsClick?: (
			payment: TablePaymentRow,
			event: React.MouseEvent
		) => void
	) => React.ReactNode;
}

// Utility functions
export const formatCurrency = (amount?: number, currency?: string) => {
	if (amount === undefined || amount === null) return "--";
	// Add thousand separator (apostrophe) for Swiss formatting
	const formattedAmount = roundCurrency(amount)
		.toFixed(2)
		.replace(/\B(?=(\d{3})+(?!\d))/g, "'");
	return `${formattedAmount}` + (currency ? ` ${currency}` : "");
};

export const formatDate = (dateString?: string) => {
	if (!dateString) return "--";
	return new Date(dateString).toLocaleDateString("de-DE");
};

export const formatDateTime = (dateString?: string) => {
	if (!dateString) return "--";
	return new Date(dateString).toLocaleString("de-DE");
};

export const formatPercentage = (rate?: number) => {
	if (rate === undefined || rate === null) return "--";
	return `${rate.toFixed(1)}%`;
};

// Expandable column for active invoices with payments
export const createExpanderColumn = <T extends BaseTableRowData>(options?: {
	showVATFooter?: boolean;
	showPaymentFooter?: "open" | "paid" | false;
}): ColumnDef<T> => ({
	id: "expander",
	header: "",
	minSize: 30,
	maxSize: 30,
	cell: ({ row }) => {
		if (!isInvoiceRow(row.original)) return null;

		return row.getCanExpand() ? (
			<Button
				variant="ghost"
				size="sm"
				className="p-0 h-6 w-6"
				onClick={(e) => {
					e.stopPropagation();
					row.toggleExpanded();
				}}
			>
				{row.getIsExpanded() ? (
					<ChevronDown className="h-4 w-4" />
				) : (
					<ChevronRight className="h-4 w-4" />
				)}
			</Button>
		) : (
			<div className="h-6 w-6" />
		);
	},
});

// Invoice number column
export const createInvoiceNumberColumn = <
	T extends BaseTableRowData,
>(options?: {
	showVATFooter?: boolean;
	showPaymentFooter?: "open" | "paid" | false;
}): ColumnDef<T> => ({
	id: "invoice_doc_number",
	header: "Rechnungsnummer",
	minSize: 120,
	enableSorting: true,
	enableColumnFilter: true,
	accessorKey: "invoice_doc_number",
	meta: {
		filterVariant: "text",
	},
	cell: ({ row }) => {
		if (isInvoiceRow(row.original)) {
			return (
				<div className="text-sm font-medium">
					{row.original.invoice_doc_number}
				</div>
			);
		}
	},
});

// Recipient column
export const createRecipientColumn = <T extends BaseTableRowData>(options?: {
	showVATFooter?: boolean;
	showPaymentFooter?: "open" | "paid" | false;
}): ColumnDef<T> => ({
	id: "recipient_name",
	header: "Empfänger",
	minSize: 160,
	enableSorting: true,
	enableColumnFilter: true,
	meta: {
		filterVariant: "text",
	},
	accessorFn: (row) => {
		if (isInvoiceRow(row)) {
			return (
				getClientDisplayName(
					{
						is_company: row.recipient_is_company,
						company_name: row.recipient_company_name,
						first_name: row.recipient_first_name,
						last_name: row.recipient_last_name,
					} as ClientEntityType,
					true
				) || ""
			);
		}
		return "";
	},
	cell: ({ row }) => {
		if (!isInvoiceRow(row.original)) return null;

		const invoiceRow = row.original;
		const recipient = getClientDisplayName(
			{
				is_company: invoiceRow.recipient_is_company,
				company_name: invoiceRow.recipient_company_name,
				first_name: invoiceRow.recipient_first_name,
				last_name: invoiceRow.recipient_last_name,
			} as ClientEntityType,
			true
		);
		const getRecipientTypeLabel = (type?: string) => {
			switch (type) {
				case "patient":
					return "Patient";
				case "guarantor":
					return "Garant";
				case "client":
					return "Kunde";
				default:
					return "Kunde";
			}
		};

		const getRecipientTypeVariant = (type?: string) => {
			switch (type) {
				case "patient":
					return "default";
				case "guarantor":
					return "secondary";
				case "client":
					return "outline";
				default:
					return "outline";
			}
		};

		return (
			<div className="flex items-center gap-2 text-sm">
				<Badge
					variant={getRecipientTypeVariant(
						invoiceRow.recipient_type || ""
					)}
					className="text-xs whitespace-nowrap w-16 items-center justify-center"
				>
					{getRecipientTypeLabel(invoiceRow.recipient_type || "")}
				</Badge>
				<span className="whitespace-nowrap">{recipient}</span>
			</div>
		);
	},
});

// Client column
export const createClientColumn = <
	T extends BaseTableRowData,
>(): ColumnDef<T> => ({
	id: "client_last_name",
	header: "Kunde",
	minSize: 140,
	enableSorting: true,
	enableColumnFilter: true,
	meta: {
		filterVariant: "text",
	},
	accessorFn: (row) => {
		if (isInvoiceRow(row)) {
			return (
				getClientDisplayName(
					{
						is_company: row.client_is_company,
						company_name: row.client_company_name,
						first_name: row.client_first_name,
						last_name: row.client_last_name,
					} as ClientEntityType,
					true
				) || ""
			);
		}
		return "";
	},
	cell: ({ row }) => {
		if (!isInvoiceRow(row.original)) return null;

		const invoiceRow = row.original;
		const client =
			getClientDisplayName(
				{
					is_company: invoiceRow.client_is_company,
					company_name: invoiceRow.client_company_name,
					first_name: invoiceRow.client_first_name,
					last_name: invoiceRow.client_last_name,
				} as ClientEntityType,
				true
			) || "";
		return <div className="text-sm">{client}</div>;
	},
});

// Sharing status column
export const createSharingColumn = <T extends BaseTableRowData>(
	onShareToggle: (invoice: T) => void
): ColumnDef<T> => ({
	id: "shared_status",
	header: "Geteilt",
	minSize: 60,
	maxSize: 60,
	cell: ({ row }) => {
		if (isInvoiceRow(row.original)) {
			const invoiceRow = row.original;
			const isCurrentlyShared = (invoice: TableInvoiceRow) => {
				return (
					invoice.shared_at &&
					(invoice.shared_until == null ||
						new Date(invoice.shared_until) > new Date())
				);
			};

			const isClientConnected =
				invoiceRow.client_connected_organization_id != null;

			const handleClick = (e: React.MouseEvent) => {
				e.stopPropagation(); // Prevent row expansion
				onShareToggle(invoiceRow);
			};

			const getTooltipTitle = () => {
				if (!isClientConnected) {
					return "Teilen ist nur für verbundene Kunden möglich";
				}
				return isCurrentlyShared(invoiceRow)
					? "Teilen aufheben"
					: "Rechnung teilen";
			};

			const getIconColor = () => {
				if (!isClientConnected) return "text-gray-200";
				if (isCurrentlyShared(invoiceRow)) return "text-green-600";
				return "text-gray-400";
			};

			return (
				<Tooltip title={getTooltipTitle()}>
					<span>
						<IconButton
							size="small"
							onClick={handleClick}
							disabled={!isClientConnected}
						>
							<Share2 className={`h-4 w-4 ${getIconColor()}`} />
						</IconButton>
					</span>
				</Tooltip>
			);
		}
		return null;
	},
});

// Invoice amount column
export const createInvoiceAmountColumn = <
	T extends BaseTableRowData,
>(options?: {
	showVATFooter?: boolean;
	showPaymentFooter?: "open" | "paid" | false;
}): ColumnDef<T> => ({
	id: "invoice_value",
	header: "Betrag",
	minSize: 120,
	enableSorting: true,
	enableColumnFilter: true,
	meta: {
		filterVariant: "range",
	},
	accessorKey: "invoice_value",
	cell: ({ row }) => {
		if (!isInvoiceRow(row.original)) return null;

		const invoiceRow = row.original;
		return (
			<div className="text-right text-sm font-mono whitespace-nowrap">
				{formatCurrency(invoiceRow.invoice_value || 0)}
			</div>
		);
	},
});

// Currency column
export const createCurrencyColumn = <
	T extends BaseTableRowData,
>(): ColumnDef<T> => ({
	id: "invoice_currency",
	header: "Währung",
	minSize: 80,
	maxSize: 100,
	enableSorting: true,
	enableColumnFilter: true,
	accessorKey: "invoice_currency",
	cell: ({ row }) => {
		if (!isInvoiceRow(row.original)) return null;

		const currency = row.original.invoice_currency || "CHF";
		return <div className="text-sm font-medium">{currency}</div>;
	},
});

// Invoice date column
export const createInvoiceDateColumn = <T extends BaseTableRowData>(options?: {
	showPaymentFooter?: "open" | "paid" | false;
}): ColumnDef<T> => ({
	id: "invoice_date",
	header: "Rechnungsdatum",
	minSize: 110,
	enableSorting: true,
	enableColumnFilter: true,
	meta: {
		filterVariant: "date",
	},
	accessorKey: "invoice_date",
	cell: ({ row }) => {
		if (!isInvoiceRow(row.original)) return null;

		return (
			<div className="text-sm">
				{formatDate(row.original.invoice_date || "")}
			</div>
		);
	},
});

// Cancellation date column (for canceled invoices)
export const createCancellationDateColumn = <
	T extends BaseTableRowData & { invoice_canceled_at?: string | null },
>(): ColumnDef<T> => ({
	id: "invoice_canceled_at",
	header: "Storniert am",
	minSize: 110,
	enableSorting: true,
	accessorKey: "invoice_canceled_at",
	cell: ({ row }) => {
		if (isInvoiceRow(row.original)) {
			return (
				<div className="text-sm text-red-600">
					{formatDate(
						"invoice_canceled_at" in row.original
							? row.original.invoice_canceled_at || ""
							: undefined
					)}
				</div>
			);
		}
		return null;
	},
});

// Cancellation reason column (for canceled invoices)
export const createCancellationReasonColumn = <
	T extends BaseTableRowData & { reason_for_cancelation?: string | null },
>(): ColumnDef<T> => ({
	id: "reason_for_cancelation",
	header: "Stornierungsgrund",
	minSize: 200,
	enableSorting: true,
	accessorKey: "reason_for_cancelation",
	cell: ({ row }) => {
		if (!isInvoiceRow(row.original)) return null;

		const reason =
			("reason_for_cancelation" in row.original
				? row.original.reason_for_cancelation
				: null) || "Kein Grund angegeben";
		return (
			<div className="text-sm max-w-xs truncate" title={reason}>
				{reason}
			</div>
		);
	},
});

// Payment status column (for canceled invoices)
export const createPaymentStatusColumn = <
	T extends BaseTableRowData,
>(): ColumnDef<T> => ({
	id: "payment_status",
	header: "Zahlungen",
	minSize: 80,
	enableSorting: false,
	cell: ({ row }) => {
		if (isInvoiceRow(row.original)) {
			const invoice = row.original;
			const hasPayments =
				invoice.payments &&
				(invoice.payments as unknown as TablePaymentRow[]).length > 0;

			if (hasPayments) {
				return (
					<div className="flex items-center">
						<div
							className="w-4 h-4 rounded-full bg-green-500"
							title="Hatte Zahlungen vor Stornierung"
						></div>
					</div>
				);
			}

			return (
				<div className="flex items-center">
					<div
						className="w-4 h-4 rounded-full bg-gray-300"
						title="Keine Zahlungen vor Stornierung"
					></div>
				</div>
			);
		}
		return null;
	},
});

// ZAK status column
export const createZakStatusColumn = <
	T extends BaseTableRowData,
>(): ColumnDef<T> => ({
	id: "zak_status",
	header: "ZAK",
	minSize: 60,
	maxSize: 60,
	cell: ({ row }) => {
		if (isInvoiceRow(row.original)) {
			const invoiceRow = row.original;
			const isSentToZak = invoiceRow.is_sent_to_zak;

			return (
				<Tooltip
					title={
						isSentToZak
							? "An ZAK gesendet"
							: "Nicht an ZAK gesendet"
					}
				>
					<div className="flex">
						<Send
							className={`h-4 w-4 ${
								isSentToZak ? "text-green-600" : "text-gray-400"
							}`}
						/>
					</div>
				</Tooltip>
			);
		}
		return null;
	},
});

const REMINDER_STAGE_COLORS = ["text-amber-500", "text-orange-600", "text-red-600"];

const getReminderStatus = (invoice: TableInvoiceRow) => {
	const stage = invoice.reminder_stage ?? 0;
	const pauseLabel = invoice.reminder_paused
		? `Mahnstopp ${
				invoice.reminder_paused_until
					? `bis ${formatReminderDate(invoice.reminder_paused_until)}`
					: "unbefristet"
			}${
				invoice.reminder_stop_reason
					? `: ${invoice.reminder_stop_reason}`
					: ""
			}`
		: null;
	const label = [stage > 0 ? getReminderStageLabel(stage) : null, pauseLabel]
		.filter(Boolean)
		.join(" · ");
	const color =
		stage > 0
			? REMINDER_STAGE_COLORS[
					Math.min(stage, REMINDER_STAGE_COLORS.length) - 1
				]
			: "text-slate-500";
	return { stage, label, color, isPaused: !!pauseLabel };
};

// Actions column
export const createActionsColumn = <T extends BaseTableRowData>(actions: {
	onOpenFile?: (fileId: string) => void;
	onOpenFileInNewTab?: (fileId: string) => void;
	onPrintAsCopy?: (invoice: TableInvoiceRow) => void;
	onOpenPaymentDialog?: (invoice: TableInvoiceRow) => void;
	onBookRemainderAsLoss?: (invoice: TableInvoiceRow) => void;
	onShareToggle?: (invoice: TableInvoiceRow) => void;
	onCancelInvoice?: (invoiceId: number) => void;
	onSendToZak?: (invoiceId: number) => void;
	onShowJobDocuments?: (invoice: TableInvoiceRow) => void;
	reminderActions?: InvoiceReminderActions;
	onPaymentActionsClick?: (
		payment: TablePaymentRow,
		event: React.MouseEvent
	) => void;
	isZakEnabled?: boolean;
	features?: any; // TableFeatures type import would be circular
}): ColumnDef<T> => ({
	id: "actions",
	header: "",
	minSize: 132,
	maxSize: 132,
	cell: ({ row }) => {
		if (!actions.features) return null;
		if (isPaymentRow(row.original)) {
			const payment = row.original as TablePaymentRow;

			if (!actions.onPaymentActionsClick) return null;

			return (
				<div className="flex">
					<Button
						variant="ghost"
						size="sm"
						className="h-6 w-6 p-0"
						onClick={(e) => {
							e.stopPropagation();
							actions.onPaymentActionsClick!(payment, e);
						}}
						aria-label="Aktionen öffnen"
					>
						<MoreHorizontal className="h-4 w-4" />
					</Button>
				</div>
			);
		}

		if (isInvoiceRow(row.original)) {
			const invoice = row.original as TableInvoiceRow;
			const reminderStatus = getReminderStatus(invoice);
			const ReminderStatusIcon = reminderStatus.isPaused
				? PauseCircle
				: BellRing;

			return (
				<div className="flex items-center gap-1">
					{/* Existing dropdown menu */}
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button
								variant="ghost"
								size="sm"
								className="h-6 w-6 p-0"
							>
								<MoreHorizontal className="h-4 w-4" />
								<span className="sr-only">Aktionen öffnen</span>
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end">
							<SharedMenuContent
								invoice={row.original}
								onOpenFile={actions.onOpenFile || (() => {})}
								onOpenPaymentDialog={
									actions.onOpenPaymentDialog
								}
								onOpenFileInNewTab={actions.onOpenFileInNewTab}
								onPrintAsCopy={actions.onPrintAsCopy}
								onBookRemainderAsLoss={
									actions.onBookRemainderAsLoss
								}
								onShareToggle={
									actions.onShareToggle || (() => {})
								}
								onCancelInvoice={actions.onCancelInvoice}
								onSendToZak={actions.onSendToZak}
								onShowJobDocuments={actions.onShowJobDocuments}
								reminderActions={actions.reminderActions}
								isZakEnabled={actions.isZakEnabled}
								features={actions.features}
								asDropdown={true}
							/>
						</DropdownMenuContent>
					</DropdownMenu>

					{/* File icon - only shown when file_id exists */}
					{invoice.file_id && actions.onOpenFile && (
						<Tooltip title="Rechnung öffnen" arrow>
							<IconButton
								size="small"
								onClick={(e) => {
									e.stopPropagation();
									actions.onOpenFile!(invoice.file_id!);
								}}
								className="h-6 w-6"
								data-testid="open-invoice-file-button"
								aria-label="Rechnung öffnen"
							>
								<EyeIcon className="h-4 w-4" />
							</IconButton>
						</Tooltip>
					)}

					{actions.onShowJobDocuments &&
						invoice.invoice_id &&
						invoice.invoice_type !== InvoiceType.MAHNGEBUEHR && (
							<Tooltip title="Auftragsdokumente anzeigen" arrow>
								<IconButton
									size="small"
									onClick={(e) => {
										e.stopPropagation();
										actions.onShowJobDocuments!(invoice);
									}}
									className="h-6 w-6"
									data-testid="show-job-documents-button"
									aria-label="Auftragsdokumente anzeigen"
								>
									<ListChecks className="h-4 w-4" />
								</IconButton>
							</Tooltip>
						)}

					{actions.reminderActions && reminderStatus.label && (
						<div
							className="flex"
							data-testid={`reminder-stage-cell-${invoice.invoice_doc_number}`}
							data-stage={reminderStatus.stage}
						>
							<TooltipWrapper
								title={`${reminderStatus.label} – Mahnverlauf anzeigen`}
							>
								<Button
									variant="ghost"
									size="sm"
									className="h-6 w-6 p-0"
									onClick={(e) => {
										e.stopPropagation();
										const reminderRef =
											toReminderInvoiceRef(invoice);
										if (reminderRef) {
											actions.reminderActions!.onShowReminderHistory(
												reminderRef
											);
										}
									}}
									data-testid="show-reminder-history-button"
									aria-label={reminderStatus.label}
								>
									<ReminderStatusIcon
										className={cn(
											"h-4 w-4",
											reminderStatus.color
										)}
									/>
								</Button>
							</TooltipWrapper>
						</div>
					)}
				</div>
			);
		}
		return null;
	},
});

// VAT Tax Rate column
export const createVATRateColumn = <T extends BaseTableRowData>(options?: {
	showVATFooter?: boolean;
	showPaymentFooter?: "open" | "paid" | false;
}): ColumnDef<T> => ({
	id: "tax_rate",
	header: "MwSt-Satz",
	minSize: 80,
	enableSorting: true,
	enableColumnFilter: true,
	accessorKey: "effective_tax_rate",
	cell: ({ row }) => {
		if (isInvoiceRow(row.original)) {
			const invoiceRow = row.original;
			return (
				<div className="text-sm font-mono">
					{formatPercentage(invoiceRow.effective_tax_rate || 0)}
				</div>
			);
		}
		return null;
	},
});

// VAT Net Amount column (invoice value minus VAT)
export const createVATNetAmountColumn = <T extends BaseTableRowData>(options?: {
	showVATFooter?: boolean;
	showPaymentFooter?: "open" | "paid" | false;
}): ColumnDef<T> => ({
	id: "net_amount",
	header: "Netto",
	minSize: 100,
	enableSorting: true,
	enableColumnFilter: true,
	meta: {
		filterVariant: "range",
	},
	accessorFn: (row) => {
		if (isInvoiceRow(row)) {
			const netAmount = (row.invoice_value || 0) - (row.vat_amount || 0);
			return netAmount;
		}
		return 0;
	},
	cell: ({ row }) => {
		if (isInvoiceRow(row.original)) {
			const invoiceRow = row.original;
			const netAmount =
				(invoiceRow.invoice_value || 0) - (invoiceRow.vat_amount || 0);
			return (
				<div className="text-right text-sm font-mono">
					{formatCurrency(netAmount)}
				</div>
			);
		}
		return "";
	},
});

// VAT Amount column
export const createVATAmountColumn = <T extends BaseTableRowData>(options?: {
	showVATFooter?: boolean;
	showPaymentFooter?: "open" | "paid" | false;
}): ColumnDef<T> => ({
	id: "vat_amount",
	header: "MwSt",
	minSize: 100,
	enableSorting: true,
	enableColumnFilter: true,
	meta: {
		filterVariant: "range",
	},
	accessorKey: "vat_amount",
	cell: ({ row }) => {
		if (!isInvoiceRow(row.original)) return "";

		const invoice = row.original;
		const vatAmount = invoice.vat_amount || 0;

		return (
			<div className="text-right text-sm font-mono">
				{formatCurrency(vatAmount)}
			</div>
		);
	},
});

// VAT Gross Amount column (same as invoice value, but for clarity)
export const createVATGrossAmountColumn = <
	T extends BaseTableRowData,
>(options?: {
	showVATFooter?: boolean;
	showPaymentFooter?: "open" | "paid" | false;
}): ColumnDef<T> => ({
	id: "gross_amount",
	header: "Brutto",
	minSize: 100,
	enableSorting: true,
	accessorKey: "invoice_value",
	cell: ({ row }) => {
		if (isPaymentRow(row.original)) {
			const paymentRow = row.original;
			if (paymentRow.payment_id === null) {
				// For loss allocations, use the payment currency or fall back to CHF
				return (
					<div className="pl-6 text-right text-sm font-mono text-red-600 whitespace-nowrap">
						-{formatCurrency(Math.abs(paymentRow.allocated_amount))}
					</div>
				);
			}
			return (
				<div className="pl-6 text-right text-sm font-mono text-green-600 whitespace-nowrap">
					{formatCurrency(paymentRow.allocated_amount)}
				</div>
			);
		}

		if (isInvoiceRow(row.original)) {
			const invoiceRow = row.original;
			return (
				<div className="text-right text-sm font-mono font-semibold">
					{formatCurrency(invoiceRow.invoice_value || 0)}
				</div>
			);
		}
		return "";
	},
});

// Effective Tax Rate column
export const createEffectiveTaxRateColumn = <
	T extends BaseTableRowData,
>(options?: {
	showPaymentFooter?: "open" | "paid" | false;
}): ColumnDef<T> => ({
	id: "effective_tax_rate",
	header: "MwSt-Satz",
	minSize: 80,
	enableSorting: true,
	accessorKey: "effective_tax_rate",
	cell: ({ row }) => {
		if (isInvoiceRow(row.original)) {
			const invoiceRow = row.original;
			const rate = invoiceRow.effective_tax_rate || 0;
			return <div className="text-right text-sm">{rate.toFixed(1)}%</div>;
		}
		return "";
	},
});

// Payment Percentage Column with linear progress bar
export const createPaymentPercentageColumn = <
	T extends BaseTableRowData,
>(options?: {
	showPaymentFooter?: "open" | "paid" | false;
}): ColumnDef<T> => ({
	id: "payment_percentage",
	header: "Zahlungsgrad",
	minSize: 120,
	enableSorting: true,
	enableColumnFilter: true,
	meta: {
		filterVariant: "range",
	},
	accessorKey: "payment_percentage",
	cell: ({ row }) => {
		if (isInvoiceRow(row.original)) {
			const invoiceRow = row.original;
			const invoiceValue = invoiceRow.invoice_value || 0;

			// Calculate total paid (excluding losses) and total loss separately
			const payments =
				(invoiceRow.payments as unknown as TablePaymentRow[]) || [];
			const totalPaid = payments
				.filter(
					(payment: TablePaymentRow) =>
						!payment.allocation_canceled_at &&
						payment.payment_id !== null
				)
				.reduce(
					(sum: number, payment: TablePaymentRow) =>
						sum + (payment.allocated_amount || 0),
					0
				);

			const totalLoss = payments
				.filter(
					(payment: TablePaymentRow) =>
						!payment.allocation_canceled_at &&
						payment.payment_id === null
				)
				.reduce(
					(sum: number, payment: TablePaymentRow) =>
						sum + (payment.allocated_amount || 0),
					0
				);

			// Calculate percentage
			const percentage = applyRounding(
				(invoiceRow.payment_percentage || 0) * 100,
				RoundToType.TO_FIRST_DECIMAL
			);
			const isFullyPaid = percentage >= 99.9;
			const isFinishedWithLoss =
				totalLoss > 0 && totalPaid + totalLoss >= invoiceValue * 0.999;

			// Determine colors based on payment status
			let progressColor = "bg-red-500";
			let textColor = "text-red-600";

			if (isFullyPaid) {
				progressColor = "bg-green-500";
				textColor = "text-green-600";
			} else if (percentage > 0) {
				progressColor = "bg-yellow-500";
				textColor = "text-yellow-600";
			}

			return (
				<div>
					<div className="flex items-center gap-2">
						<div className="flex-1 bg-gray-200 rounded-full h-2">
							<div
								className={`h-2 rounded-full ${progressColor}`}
								style={{
									width: `${Math.min(percentage, 100)}%`,
								}}
							/>
						</div>
						<span className={`text-sm font-medium ${textColor}`}>
							{percentage.toFixed(1)}%
						</span>
						{isFinishedWithLoss && (
							<Tooltip
								title={`Abgeschlossen mit Verlust (CHF ${totalLoss.toFixed(
									2
								)})`}
							>
								<AlertCircle className="w-4 h-4 text-orange-600" />
							</Tooltip>
						)}
					</div>
				</div>
			);
		}
		return null;
	},
});
export const createPaidAmountColumn = <T extends BaseTableRowData>(options?: {
	showPaymentFooter?: "open" | "paid" | false;
}): ColumnDef<T> => ({
	accessorKey: "total_allocated_amount",
	header: "Bezahlt",
	size: 120,
	cell: ({ row }) => {
		if (!isInvoiceRow(row.original)) return null;

		const invoice = row.original as any;
		const paidAmount = invoice.total_allocated_amount || 0;
		const formattedAmount = formatCurrency(paidAmount);

		return (
			<div className="text-right">
				<span className="font-semibold font-mono text-green-600">
					{formattedAmount}
				</span>
			</div>
		);
	},
});

export const createLatestPaymentDateColumn = <
	T extends BaseTableRowData,
>(options?: {
	showPaymentFooter?: "open" | "paid" | false;
}): ColumnDef<T> => ({
	accessorKey: "valuta_date",
	header: "Letzter Zahlungseingang",
	size: 140,
	cell: ({ row }) => {
		if (!isInvoiceRow(row.original)) return null;

		const invoice = row.original as any;
		const date = invoice.valuta_date;

		if (!date) return <span className="text-gray-400">-</span>;

		return (
			<div>
				<span className="text-sm">{formatDate(date)}</span>
			</div>
		);
	},
});

// Open Amount column for open invoices
export const createOpenAmountColumn = <T extends BaseTableRowData>(options?: {
	showPaymentFooter?: "open" | "paid" | false;
}): ColumnDef<T> => ({
	id: "open_amount",
	header: "Offen",
	minSize: 100,
	enableSorting: true,
	accessorFn: (row) => {
		if (isInvoiceRow(row)) {
			const invoiceValue = row.invoice_value || 0;
			const totalAllocated =
				(row.payments as unknown as TablePaymentRow[])
					?.filter((payment) => !payment.allocation_canceled_at)
					.reduce((sum, payment) => {
						return sum + (payment.allocated_amount || 0);
					}, 0) || 0;
			return Math.max(0, invoiceValue - totalAllocated);
		}
		return 0;
	},
	cell: ({ row }) => {
		if (isInvoiceRow(row.original)) {
			const invoiceValue = row.original.invoice_value || 0;
			const totalAllocated =
				(row.original.payments as unknown as TablePaymentRow[])
					?.filter((payment) => !payment.allocation_canceled_at)
					.reduce((sum, payment) => {
						return sum + (payment.allocated_amount || 0);
					}, 0) || 0;
			const openAmount = Math.max(0, invoiceValue - totalAllocated);

			return (
				<div
					className={cn(
						"text-right text-sm font-mono font-semibold",
						openAmount == 0 ? "" : "text-red-600"
					)}
				>
					{formatCurrency(openAmount)}
				</div>
			);
		}
		return "";
	},
});

// Build payment row columns (independent from invoice columns)
export const buildPaymentRowColumns = (): PaymentColumnDef[] => {
	return [
		// Payment status
		{
			id: "payment-status",
			header: "Status",
			width: 90,
			minWidth: 90,
			render: (payment: TablePaymentRow) => {
				const isPaymentCanceled = !!payment.payment_canceled_at;
				const PaymentExists = !!payment.payment_id;
				const isAllocationCanceled = !!payment.allocation_canceled_at;

				if (isPaymentCanceled && isAllocationCanceled) {
					return (
						<div className="text-sm text-gray-500 font-medium">
							Storniert
						</div>
					);
				}

				if (
					isAllocationCanceled &&
					PaymentExists &&
					!isPaymentCanceled &&
					payment.allocation_type === 2
				) {
					return (
						<div className="text-sm text-orange-600 font-medium">
							Akontozuweisung storniert
						</div>
					);
				}

				if (
					isAllocationCanceled &&
					PaymentExists &&
					!isPaymentCanceled
				) {
					return (
						<div className="text-sm text-orange-600 font-medium">
							Umgebucht als Akonto
						</div>
					);
				}

				if (!PaymentExists) {
					return (
						<div className="text-sm text-red-600 font-medium">
							Verlust{isAllocationCanceled ? " storniert" : ""}
						</div>
					);
				}

				return (
					<div className="text-sm text-green-600 font-medium">
						Aktiv
					</div>
				);
			},
		},
		// Payment type
		{
			id: "payment-type",
			header: "Zahlungsart",
			width: 150,
			minWidth: 150,
			render: (payment: TablePaymentRow) => {
				if (payment.payment_id === null) return null;
				const isReallocated =
					payment.allocation_type === 2 &&
					!payment.allocation_canceled_at;
				return (
					<div className="text-sm text-gray-900">
						{isReallocated
							? "Akonto"
							: MapPaymentTypeToString[
									payment.payment_type as unknown as PaymentType
								] || "Zahlung"}
					</div>
				);
			},
		},
		// Payment amount
		{
			id: "payment-amount",
			header: "Betrag",
			width: 100,
			minWidth: 100,
			render: (payment: TablePaymentRow) => {
				if (payment.payment_id === null) {
					const currency = payment.payment_currency || "CHF";
					return (
						<div className="text-right text-sm font-mono text-red-600 whitespace-nowrap">
							-
							{formatCurrency(
								Math.abs(payment.allocated_amount),
								currency
							)}
						</div>
					);
				}
				return (
					<div className="text-right text-sm font-mono text-green-600 whitespace-nowrap">
						{formatCurrency(
							payment.allocated_amount,
							payment.payment_currency || "CHF"
						)}
					</div>
				);
			},
		},
		// Payment date
		{
			id: "payment-date",
			header: "Datum",
			width: 90,
			minWidth: 90,
			render: (payment: TablePaymentRow) => {
				if (payment.payment_id === null) {
					return (
						<div className="text-sm text-red-600">
							{formatDate(payment.allocation_created_at)}
						</div>
					);
				}
				return (
					<div className="text-sm text-gray-900">
						{formatDate(payment.payment_valuta_date || "")}
					</div>
				);
			},
		},
		// Payment note
		{
			id: "payment-note",
			header: "Notiz",
			width: 300,
			minWidth: 200,
			render: (payment: TablePaymentRow) => {
				const isLoss = payment.payment_id === null;
				const note = isLoss
					? payment.allocation_note
					: payment.payment_note_1;
				if (!note) return null;
				return (
					<div
						className={`text-xs italic truncate ${
							isLoss ? "text-red-600" : "text-gray-600"
						}`}
						title={note}
					>
						{isLoss && (
							<span className="font-semibold">
								Verlustgrund:{" "}
							</span>
						)}
						{note}
					</div>
				);
			},
		},
		// Cancellation reason (only for canceled payments)
		{
			id: "payment-cancellation-reason",
			header: "Stornierungsgrund",
			width: 300,
			minWidth: 200,
			render: (payment: TablePaymentRow) => {
				const isPaymentCanceled = !!payment.payment_canceled_at;
				const isAllocationCanceled = !!payment.allocation_canceled_at;

				if (!isPaymentCanceled && !isAllocationCanceled) return null;

				// For rebooked allocations (only allocation canceled, not payment), use allocation_note
				// For fully canceled payments (both canceled), use payment_note_2
				let reason: string | null | undefined;
				if (isAllocationCanceled && !isPaymentCanceled) {
					// Rebooked as Akonto
					reason = payment.allocation_note;
				} else if (isPaymentCanceled && isAllocationCanceled) {
					// Fully canceled
					reason = payment.payment_note_2 || payment.allocation_note;
				} else {
					reason = payment.allocation_note;
				}

				if (!reason)
					return <span className="text-xs text-gray-400">-</span>;

				return (
					<div
						className="text-xs text-gray-600 italic truncate"
						title={reason}
					>
						{reason}
					</div>
				);
			},
		},
	];
};

// ============================================
// PAYMENT-CENTRIC COLUMNS (for Zahlungsjournal)
// ============================================

export interface InvoiceAllocationColumnDef {
	id: string;
	header: string;
	width?: number;
	minWidth?: number;
	maxWidth?: number;
	render: (allocation: InvoiceAllocationSubRow) => React.ReactNode;
}

export const createPaymentExpanderColumn = <
	T extends PaymentJournalTableRowData,
>(): ColumnDef<T> => ({
	id: "expander",
	header: "",
	size: 40,
	minSize: 40,
	maxSize: 40,
	cell: ({ row }) => {
		// For sub-rows (invoice allocations), don't show expander
		if (isInvoiceSubRow(row.original)) {
			return <div className="w-6 pl-4" />;
		}

		// For primary payment rows, always show expander if there are invoice allocations
		const original = row.original as PaymentJournalRowWithSubRows;
		const hasAllocations =
			original.invoice_allocations &&
			original.invoice_allocations.length > 0;

		if (!hasAllocations) return <div className="w-6" />;

		return (
			<Button
				variant="ghost"
				size="sm"
				className="p-0 h-6 w-6"
				onClick={(e) => {
					e.stopPropagation();
					row.toggleExpanded();
				}}
			>
				{row.getIsExpanded() ? (
					<ChevronDown className="h-4 w-4" />
				) : (
					<ChevronRight className="h-4 w-4" />
				)}
			</Button>
		);
	},
});

export const createPaymentJournalValueColumn = <
	T extends PaymentJournalTableRowData,
>(): ColumnDef<T> => ({
	id: "payment_value",
	header: "Betrag",
	minSize: 120,
	enableSorting: true,
	accessorKey: "payment_value",
	cell: ({ row }) => {
		if (isInvoiceSubRow(row.original)) {
			const allocation = row.original;
			return (
				<div className="pl-6 text-right text-sm font-mono text-gray-600 whitespace-nowrap">
					{formatCurrency(
						allocation.allocated_amount,
						allocation.invoice_currency
					)}
				</div>
			);
		}

		if (isPaymentPrimaryRow(row.original)) {
			const payment = row.original;
			return (
				<div className="text-right text-sm font-mono font-semibold text-green-600 whitespace-nowrap">
					{formatCurrency(
						payment.payment_value,
						payment.payment_currency
					)}
				</div>
			);
		}
		return null;
	},
});

export const createPaymentJournalTypeColumn = <
	T extends PaymentJournalTableRowData,
>(): ColumnDef<T> => ({
	id: "payment_type",
	header: "Zahlungsart",
	minSize: 130,
	enableSorting: true,
	accessorKey: "payment_type",
	cell: ({ row }) => {
		if (!isPaymentPrimaryRow(row.original)) return null;

		const payment = row.original;
		return (
			<div className="text-sm">
				{MapPaymentTypeToString[payment.payment_type as PaymentType] ||
					"Zahlung"}
			</div>
		);
	},
});

export const createPaymentJournalValutaDateColumn = <
	T extends PaymentJournalTableRowData,
>(): ColumnDef<T> => ({
	id: "valuta_date",
	header: "Valutadatum",
	minSize: 110,
	enableSorting: true,
	accessorKey: "valuta_date",
	cell: ({ row }) => {
		if (isInvoiceSubRow(row.original)) {
			const allocation = row.original;
			return (
				<div className="pl-6 text-sm text-gray-500">
					{formatDate(allocation.invoice_date)}
				</div>
			);
		}

		if (isPaymentPrimaryRow(row.original)) {
			const payment = row.original;
			return (
				<div className="text-sm font-medium">
					{formatDate(payment.valuta_date || "")}
				</div>
			);
		}
		return null;
	},
});

export const createPaymentJournalSenderColumn = <
	T extends PaymentJournalTableRowData,
>(): ColumnDef<T> => ({
	id: "sender",
	header: "Absender",
	minSize: 220,
	enableSorting: false,
	cell: ({ row }) => {
		if (isInvoiceSubRow(row.original)) {
			const allocation = row.original;
			const displayName = getClientDisplayName(
				{
					is_company: allocation.recipient_is_company,
					company_name: allocation.recipient_company_name,
					first_name: allocation.recipient_first_name,
					last_name: allocation.recipient_last_name,
				} as ClientEntityType,
				true
			);
			return (
				<div
					className="pl-6 text-sm text-gray-600 truncate"
					title={displayName}
				>
					{displayName}
				</div>
			);
		}

		if (isPaymentPrimaryRow(row.original)) {
			const payment = row.original;
			const sender = payment.primary_sender;
			const senderType = payment.primary_sender_type;

			if (!sender) return <span className="text-gray-400">-</span>;

			const displayName = getClientDisplayName(
				{
					is_company: sender.is_company,
					company_name: sender.company_name,
					first_name: sender.first_name,
					last_name: sender.last_name,
				} as ClientEntityType,
				true
			);

			const getSenderTypeLabel = (type?: string | null) => {
				switch (type) {
					case "patient":
						return "Patient";
					case "guarantor":
						return "Garant";
					case "client":
						return "Kunde";
					default:
						return "Kunde";
				}
			};

			const getSenderTypeVariant = (
				type?: string | null
			): "default" | "secondary" | "outline" => {
				switch (type) {
					case "patient":
						return "default";
					case "guarantor":
						return "secondary";
					case "client":
						return "outline";
					default:
						return "outline";
				}
			};

			return (
				<div className="flex items-center gap-2 text-sm">
					<Badge
						variant={getSenderTypeVariant(senderType)}
						className="text-xs whitespace-nowrap w-16 items-center justify-center"
					>
						{getSenderTypeLabel(senderType)}
					</Badge>
					<span className="truncate" title={displayName}>
						{displayName}
					</span>
				</div>
			);
		}
		return null;
	},
});

export const createPaymentJournalInvoiceReferencesColumn = <
	T extends PaymentJournalTableRowData,
>(): ColumnDef<T> => ({
	id: "invoice_references",
	header: "Rechnungen",
	minSize: 200,
	enableSorting: false,
	cell: ({ row }) => {
		if (isInvoiceSubRow(row.original)) {
			const allocation = row.original;
			const isCanceled =
				allocation.allocation_is_canceled ||
				allocation.invoice_is_canceled;
			return (
				<div
					className={`pl-6 text-sm font-medium flex items-center gap-2 ${
						isCanceled ? "text-gray-400" : ""
					}`}
				>
					{allocation.invoice_doc_number}
					{isCanceled && (
						<span className="px-1.5 py-0.5 text-xs font-medium bg-red-100 text-red-700 rounded">
							storniert
						</span>
					)}
				</div>
			);
		}

		if (isPaymentPrimaryRow(row.original)) {
			const payment = row.original;
			return (
				<div className="text-sm">
					<span className="font-medium">{payment.invoice_count}</span>
					<span
						className="text-gray-500 ml-1 truncate"
						title={payment.invoice_doc_numbers || ""}
					>
						({payment.invoice_doc_numbers || "-"})
					</span>
				</div>
			);
		}
		return null;
	},
});

export const createPaymentJournalNoteColumn = <
	T extends PaymentJournalTableRowData,
>(): ColumnDef<T> => ({
	id: "payment_note",
	header: "Notiz",
	minSize: 200,
	cell: ({ row }) => {
		if (!isPaymentPrimaryRow(row.original)) return null;

		const payment = row.original;
		const note = payment.payment_note_1;

		if (!note) return null;

		return (
			<div className="text-xs text-gray-600 italic truncate" title={note}>
				{note}
			</div>
		);
	},
});

export const createPaymentJournalCurrencyColumn = <
	T extends PaymentJournalTableRowData,
>(): ColumnDef<T> => ({
	id: "payment_currency",
	header: "Währung",
	minSize: 80,
	maxSize: 100,
	enableSorting: true,
	accessorKey: "payment_currency",
	cell: ({ row }) => {
		if (!isPaymentPrimaryRow(row.original)) return null;

		const currency = row.original.payment_currency || "CHF";
		return <div className="text-sm font-medium">{currency}</div>;
	},
});

export const buildInvoiceAllocationSubRowColumns =
	(): InvoiceAllocationColumnDef[] => {
		return [
			{
				id: "allocation-invoice",
				header: "Rechnung",
				width: 220,
				minWidth: 180,
				render: (allocation: InvoiceAllocationSubRow) => {
					const hasNoInvoice = !allocation.invoice_id;

					// No invoice - unredeemed akonto credit
					if (hasNoInvoice) {
						return (
							<div className="flex items-center gap-2">
								<span className="px-2 py-1 text-xs font-medium bg-amber-100 text-amber-800 rounded border border-amber-200">
									Akontoguthaben
								</span>
							</div>
						);
					}

					// Map invoice type to label
					const getInvoiceTypeLabel = (type: number): string => {
						switch (type) {
							case 0:
								return "Einzelrechnung";
							case 1:
								return "Barrechnung";
							case 11:
								return "Kartenzahlungsquittung";
							case 2:
								return "Akontorechnung";
							case 20:
								return "Monatsrechnung";
							case 99:
								return "Akontorechnung";
							case 98:
								return "Rückvergütung";
							case InvoiceType.MAHNGEBUEHR:
								return "Mahngebühr";
							default:
								return "Rechnung";
						}
					};

					const invoiceTypeLabel = getInvoiceTypeLabel(
						allocation.invoice_type
					);
					const isPseudoInvoice = allocation.invoice_type === 99;
					const isCanceled =
						allocation.allocation_is_canceled ||
						allocation.invoice_is_canceled;

					return (
						<div
							className={`flex items-center gap-2 ${
								isCanceled ? "text-gray-400" : ""
							}`}
						>
							{!isPseudoInvoice && (
								<span className="text-sm font-medium">
									{allocation.invoice_doc_number}
								</span>
							)}
							<span className="px-1.5 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded">
								{invoiceTypeLabel}
							</span>
							{isCanceled && (
								<span className="px-1.5 py-0.5 text-xs font-medium bg-red-100 text-red-700 rounded">
									storniert
								</span>
							)}
						</div>
					);
				},
			},
			{
				id: "allocation-amount",
				header: "Zugewiesen",
				width: 120,
				minWidth: 100,
				render: (allocation: InvoiceAllocationSubRow) => (
					<div className="text-right text-sm font-mono whitespace-nowrap">
						{formatCurrency(
							allocation.allocated_amount,
							allocation.invoice_currency
						)}
					</div>
				),
			},
			{
				id: "allocation-invoice-value",
				header: "Rechnungsbetrag",
				width: 130,
				minWidth: 110,
				render: (allocation: InvoiceAllocationSubRow) => {
					if (!allocation.invoice_id) {
						return (
							<div className="text-right text-sm text-gray-400 whitespace-nowrap">
								–
							</div>
						);
					}
					return (
						<div className="text-right text-sm font-mono text-gray-600 whitespace-nowrap">
							{formatCurrency(
								allocation.invoice_value,
								allocation.invoice_currency
							)}
						</div>
					);
				},
			},
			{
				id: "allocation-recipient",
				header: "Empfänger",
				width: 180,
				minWidth: 140,
				render: (allocation: InvoiceAllocationSubRow) => {
					if (!allocation.invoice_id) {
						return <div className="text-sm text-gray-400">–</div>;
					}
					const displayName = getClientDisplayName(
						{
							is_company: allocation.recipient_is_company,
							company_name: allocation.recipient_company_name,
							first_name: allocation.recipient_first_name,
							last_name: allocation.recipient_last_name,
						} as ClientEntityType,
						true
					);
					return (
						<div className="text-sm truncate" title={displayName}>
							{displayName}
						</div>
					);
				},
			},
			{
				id: "allocation-invoice-date",
				header: "Rechnungsdatum",
				width: 100,
				minWidth: 90,
				render: (allocation: InvoiceAllocationSubRow) => {
					if (!allocation.invoice_id) {
						return <div className="text-sm text-gray-400">–</div>;
					}
					return (
						<div className="text-sm text-gray-600">
							{formatDate(allocation.invoice_date)}
						</div>
					);
				},
			},
		];
	};
